import React, {
  useCallback, useMemo, useRef, useState,
} from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import type { IFileWithMeta } from 'react-dropzone-uploader';
import UniversalMediaUploaderStatus from '../components/universal-media-uploader-status';
import { FileUploader } from '../shared/file-uploader';
import getLayerUploadParams from './upload-url';

interface FileUploaderCallbacks {
  startUpload: boolean;
  onUploadBegin: () => void;
  onComplete: () => void;
  onError: () => void;
  maxFiles: number;
}

interface StagedLayerFile {
  key: string;
  fileName: string;
  fileSize: number;
}

interface Props {
  fileUploaderProps: FileUploaderCallbacks;
  setLayerFile: (key: string, name: string, fileSize: number) => void;
  acceptedFileTypes: string;
  acceptedFilesName: string;
  apiToken?: string;
  currentFileName?: string | null;
  currentFileSize?: number | null;
  isNew: boolean;
}

const FileLayer: React.FC<Props> = ({
  fileUploaderProps,
  setLayerFile,
  acceptedFileTypes,
  acceptedFilesName,
  apiToken,
  currentFileName,
  currentFileSize,
  isNew,
}: Props) => {
  const stagedFileRef = useRef<StagedLayerFile | null>(null);
  const [uploadingFile, setUploadingFile] = useState<Pick<StagedLayerFile, 'fileName' | 'fileSize'> | null>(null);
  const [uploadedFile, setUploadedFile] = useState<StagedLayerFile | null>(null);
  const [uploaderResetKey, setUploaderResetKey] = useState(0);

  const getPresignedUrl = useCallback(async ({ file }: IFileWithMeta) => {
    const uploadParams = await getLayerUploadParams({ file, apiToken });
    const stagedFile = {
      key: uploadParams.key,
      fileName: file.name,
      fileSize: file.size,
    };

    stagedFileRef.current = stagedFile;
    setUploadingFile(stagedFile);

    return uploadParams;
  }, [apiToken]);

  const handleUploadBegin = useCallback(() => {
    fileUploaderProps.onUploadBegin();
  }, [fileUploaderProps]);

  const handleComplete = useCallback(() => {
    const stagedFile = stagedFileRef.current;

    if (stagedFile) {
      setLayerFile(stagedFile.key, stagedFile.fileName, stagedFile.fileSize);
      setUploadedFile(stagedFile);
      setUploadingFile(null);
      setUploaderResetKey((resetKey) => resetKey + 1);
    }

    fileUploaderProps.onComplete();
  }, [fileUploaderProps, setLayerFile]);

  const handleError = useCallback(() => {
    stagedFileRef.current = null;
    setUploadingFile(null);
    fileUploaderProps.onError();
  }, [fileUploaderProps]);

  const currentFileStatus = useMemo(() => {
    if (uploadingFile || uploadedFile || !currentFileName) {
      return null;
    }

    return (
      <UniversalMediaUploaderStatus
        name={currentFileName}
        size={currentFileSize ?? undefined}
      />
    );
  }, [currentFileName, currentFileSize, uploadedFile, uploadingFile]);

  return (
    <Box data-testid="file-layer-uploader">
      {currentFileStatus}
      {uploadedFile ? (
        <Box>
          <UniversalMediaUploaderStatus
            name={uploadedFile.fileName}
            size={uploadedFile.fileSize}
            isCompleted
            wasRecentlyUploaded
          />
          <Typography variant="caption" color="text.secondary">
            {isNew ? 'File uploaded. Click Create Layer to save it.' : 'Replacement uploaded. Update Layer to save it.'}
          </Typography>
        </Box>
      ) : null}
      <FileUploader
        key={uploaderResetKey}
        {...fileUploaderProps}
        startUpload={false}
        onUploadBegin={handleUploadBegin}
        onComplete={handleComplete}
        onError={handleError}
        onUploadParams={getPresignedUrl}
        acceptedFileTypes={acceptedFileTypes}
        acceptedFilesName={acceptedFilesName}
        dragDropText="Drag & drop file or"
      />
    </Box>
  );
};

export default FileLayer;
